import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	CreateFundDto,
	UpdateFundDto,
	FundRes,
	FundWithDealRes,
	FundDealRes,
	LinkFundToDealDto,
	LinkPersonToFundDto,
	PersonFundLinkRes,
	FundDealWithDealRes,
	FundDealSummaryRes,
	AvailableDealsForPersonFundLinkRes,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';

/**
 * Create a new Fund (creates both Deal and Fund).
 */
export const createFund = async (args: CreateFundDto, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<FundRes>>(
		{
			endpoint: API_PATHS.FUND_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
	return response;
};

/**
 * Update an existing Fund and its underlying Deal.
 */
export const updateFund = async (
	args: UpdateFundDto & { fundId: string },
	options?: ServiceRequestOptions
) => {
	const { fundId, ...body } = args;
	const response = await makeRequest<ApiV1.Response<FundWithDealRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.FUND_FUNDID_PATCH, { fundId }),
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify(body),
		},
		{ ...options }
	);
	return response;
};

/**
 * Get all Funds that belong to a given organization.
 */
export const getFundsForOrganization = async (
	args: { organizationId: string },
	signal?: AbortSignal
) => {
	const { organizationId } = args;
	return await makeRequest<ApiV1.Response<FundWithDealRes[]>>(
		{
			endpoint: `${API_PATHS.FUND_GET}?organizationId=${encodeURIComponent(organizationId)}`,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((resp) => resp.payload ?? []);
};

/**
 * Get a Fund by ID.
 */
export const getFundById = async (args: { fundId: string }, signal?: AbortSignal) => {
	const { fundId } = args;
	const response = await makeRequest<ApiV1.Response<FundWithDealRes | null>>(
		{
			endpoint: formatUnicorn(API_PATHS.FUND_FUNDID_GET, { fundId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
	return response;
};

/**
 * Get the Fund associated with a Deal (if the Deal is a Fund).
 */
export const getFundByDealId = async (args: { dealId: string }, signal?: AbortSignal) => {
	const { dealId } = args;
	const response = await makeRequest<ApiV1.Response<FundWithDealRes | null>>(
		{
			endpoint: formatUnicorn(API_PATHS.FUND_DEAL_DEALID_GET, { dealId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
	return response;
};

/**
 * Get all deals linked to a fund.
 */
export const getDealsForFund = async (args: { fundId: string }, signal?: AbortSignal) => {
	const { fundId } = args;
	return await makeRequest<ApiV1.Response<FundDealWithDealRes[]>>(
		{
			endpoint: formatUnicorn(API_PATHS.FUND_FUNDID_LINKED_DEALS_GET, { fundId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((res) => res.payload);
};

/**
 * Get deals available to link to a Fund.
 */
export const getAvailableDealsForFund = async (args: { fundId: string }, signal?: AbortSignal) => {
	const { fundId } = args;
	return await makeRequest<ApiV1.Response<FundDealSummaryRes[]>>(
		{
			endpoint: formatUnicorn(API_PATHS.FUND_FUNDID_AVAILABLE_DEALS_GET, { fundId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((res) => res.payload);
};

/**
 * Get deals available for linking a person to a fund.
 */
export const getAvailableDealsForPersonFundLink = async (
	args: { fundId: string; personId: string },
	signal?: AbortSignal
) => {
	const { fundId, personId } = args;
	return await makeRequest<ApiV1.Response<AvailableDealsForPersonFundLinkRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.FUND_FUNDID_AVAILABLE_DEALS_FOR_PERSON_PERSONID_GET, {
				fundId,
				personId,
			}),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((res) => res.payload);
};

/**
 * Link a Fund to a Deal (Fund invests in Deal).
 */
export const linkFundToDeal = async (args: LinkFundToDealDto, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<FundDealRes>>(
		{
			endpoint: API_PATHS.FUND_DEAL_LINK_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
	return response;
};

/**
 * Unlink a Fund from a Deal.
 */
export const unlinkFundFromDeal = async (
	args: LinkFundToDealDto,
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<void>>(
		{
			endpoint: API_PATHS.FUND_DEAL_UNLINK_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
};

/**
 * Link a Person from a Fund to a Deal.
 */
export const linkPersonToFund = async (
	args: LinkPersonToFundDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<PersonFundLinkRes>>(
		{
			endpoint: API_PATHS.FUND_LINK_PERSON_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
	return response;
};

/**
 * Unlink a Person from a Fund-Deal.
 */
export const unlinkPersonFromFund = async (
	args: LinkPersonToFundDto,
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<void>>(
		{
			endpoint: API_PATHS.FUND_LINK_PERSON_DELETE,
			method: HTTP_METHOD.DELETE,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
};

/**
 * Get deal links for a fund person.
 */
export const getFundPersonDealLinks = async (args: { personId: string }, signal?: AbortSignal) => {
	const { personId } = args;
	return await makeRequest<ApiV1.Response<PersonFundLinkRes[]>>(
		{
			endpoint: formatUnicorn(API_PATHS.FUND_PERSON_PERSONID_FUND_LINKS_GET, { personId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((res) => res.payload);
};
